import {
    useEffect,
    useMemo,
    useState,
} from "react";

import DataTable from "components/table/DataTable";

import {
    getCollections,
    cancelCollection,
    submitCollection,
    verifyCollection,
    rejectCollection,
    getOpeningBalances,
    getOutstandingBalances,
} from "services/api/collectionApi";

import {
    showSuccess,
    showError,
} from "utils/toast";

import {
    collectionColumns,
} from "./columns";

import CollectionModal from "./CollectionModal";
import OpeningBalanceModal from "./OpeningBalanceModal";


export default function Collections() {

    // ============================================================
    // USER / RBAC
    // ============================================================

    const [user, setUser] =
        useState({});


    useEffect(() => {

        try {

            const storedUser =
                localStorage.getItem("user");

            if (storedUser) {

                setUser(
                    JSON.parse(storedUser) || {}
                );

            }

        } catch (error) {

            console.error(
                "Unable to parse logged-in user:",
                error
            );

            setUser({});

        }

    }, []);


    const roles =
        Array.isArray(user?.roles)
            ? user.roles
            : [];


    const isAdmin =
        roles.includes("admin") ||
        roles.includes("super_admin");


    const isManager =
        roles.includes("manager") ||
        roles.includes("team_leader") ||
        roles.includes("regional_manager");


    const isSalesPerson =
        roles.includes("sales_person");


    const canCreateCollection =
        isSalesPerson ||
        isManager ||
        isAdmin;


    const canManageOpeningBalance =
        isAdmin;


    /*
    |--------------------------------------------------------------------------
    | COLLECTION STATE
    |--------------------------------------------------------------------------
    */

    const [
        collections,
        setCollections,
    ] = useState([]);


    const [
        loading,
        setLoading,
    ] = useState(false);


    const [
        page,
        setPage,
    ] = useState(1);


    /*
    |--------------------------------------------------------------------------
    | BALANCE STATE
    |--------------------------------------------------------------------------
    */

    const [
        openingBalances,
        setOpeningBalances,
    ] = useState([]);


    const [
        outstandingBalances,
        setOutstandingBalances,
    ] = useState([]);


    const [
        balanceLoading,
        setBalanceLoading,
    ] = useState(false);


    /*
    |--------------------------------------------------------------------------
    | MODAL STATE
    |--------------------------------------------------------------------------
    */

    const [
        openCollectionModal,
        setOpenCollectionModal,
    ] = useState(false);


    const [
        openOpeningModal,
        setOpenOpeningModal,
    ] = useState(false);


    const [
        selectedCollection,
        setSelectedCollection,
    ] = useState(null);



    /*
    |--------------------------------------------------------------------------
    | LOAD COLLECTIONS
    |--------------------------------------------------------------------------
    */

    const loadCollections =
        async () => {

            try {

                setLoading(true);


                const response =
                    await getCollections(
                        page,
                        10
                    );


                setCollections(
                    response?.data?.collections || []
                );

            } catch (error) {

                console.error(
                    "Collections load error:",
                    error
                );


                showError(
                    error?.response?.data?.message ||
                    "Unable to load collections."
                );

            } finally {

                setLoading(false);

            }

        };



    /*
    |--------------------------------------------------------------------------
    | LOAD BALANCES
    |--------------------------------------------------------------------------
    */

    const loadBalances =
        async () => {

            try {

                setBalanceLoading(
                    true
                );


                const requests = [
                    getOutstandingBalances(),
                ];


                /*
                Opening balances are restricted to
                Manager/Admin in backend.
        
                Sales person can therefore fail on
                this request. We simply don't block
                the whole page.
                */

                if (
                    isAdmin ||
                    isManager
                ) {

                    requests.push(
                        getOpeningBalances()
                    );

                }


                const results =
                    await Promise.all(
                        requests
                    );


                const outstandingResponse =
                    results[0];


                const openingResponse =
                    results[1];


                setOutstandingBalances(
                    outstandingResponse
                        ?.data
                        ?.outstandingBalances ||
                    []
                );


                setOpeningBalances(
                    openingResponse
                        ?.data
                        ?.openingBalances ||
                    []
                );

            } catch (error) {

                console.error(
                    "Balance loading error:",
                    error
                );


                /*
                Keep UI usable even if one
                balance endpoint is unavailable.
                */

                setOpeningBalances(
                    []
                );

                setOutstandingBalances(
                    []
                );

            } finally {

                setBalanceLoading(
                    false
                );

            }

        };



    /*
    |--------------------------------------------------------------------------
    | LOAD EVERYTHING
    |--------------------------------------------------------------------------
    */

    const loadData =
        async () => {

            await Promise.all([
                loadCollections(),
                loadBalances(),
            ]);

        };



    useEffect(() => {

        loadCollections();

    }, [page]);


    useEffect(() => {

        loadBalances();

    }, [
        isAdmin,
        isManager,
    ]);



    /*
    |--------------------------------------------------------------------------
    | REFRESH ALL AFTER MUTATION
    |--------------------------------------------------------------------------
    */

    const refreshAll =
        async () => {

            await Promise.all([
                loadCollections(),
                loadBalances(),
            ]);

        };



    /*
    |--------------------------------------------------------------------------
    | SUMMARY CALCULATIONS
    |--------------------------------------------------------------------------
    */

    const balanceSummary =
        useMemo(() => {

            const openingTotal =
                openingBalances.reduce(
                    (
                        sum,
                        balance
                    ) =>
                        sum +
                        Number(
                            balance?.amount || 0
                        ),
                    0
                );


            const outstandingTotal =
                outstandingBalances.reduce(
                    (
                        sum,
                        balance
                    ) =>
                        sum +
                        Number(
                            balance?.balanceAmount || 0
                        ),
                    0
                );


            const collectedTotal =
                outstandingBalances.reduce(
                    (
                        sum,
                        balance
                    ) =>
                        sum +
                        Number(
                            balance?.collectedAmount || 0
                        ),
                    0
                );


            return {

                openingTotal,

                outstandingTotal,

                collectedTotal,

            };

        }, [
            openingBalances,
            outstandingBalances,
        ]);



    /*
    |--------------------------------------------------------------------------
    | FORMAT MONEY
    |--------------------------------------------------------------------------
    */

    const formatMoney =
        (value) => {

            return Number(
                value || 0
            ).toLocaleString(
                "en-IN",
                {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                }
            );

        };



    /*
    |--------------------------------------------------------------------------
    | CUSTOMER NAME
    |--------------------------------------------------------------------------
    */

    const getCustomerName =
        (item) => {

            return (
                item?.party?.name ||
                item?.school?.name ||
                "-"
            );

        };



    /*
    |--------------------------------------------------------------------------
    | CUSTOMER TYPE
    |--------------------------------------------------------------------------
    */

    const getCustomerType =
        (item) => {

            return (
                item?.balanceType
                    ? item.balanceType
                    : "-"
            );

        };



    /*
    |--------------------------------------------------------------------------
    | ADD COLLECTION
    |--------------------------------------------------------------------------
    */

    const handleAdd = () => {

        if (
            !canCreateCollection
        ) {

            showError(
                "You do not have permission to create collections."
            );

            return;

        }


        setSelectedCollection(
            null
        );


        setOpenCollectionModal(
            true
        );

    };



    /*
    |--------------------------------------------------------------------------
    | EDIT COLLECTION
    |--------------------------------------------------------------------------
    */

    const handleEdit = (
        collection
    ) => {

        setSelectedCollection(
            collection
        );


        setOpenCollectionModal(
            true
        );

    };



    /*
    |--------------------------------------------------------------------------
    | CLOSE COLLECTION MODAL
    |--------------------------------------------------------------------------
    */

    const closeCollectionModal =
        () => {

            setOpenCollectionModal(
                false
            );


            setSelectedCollection(
                null
            );

        };



    /*
    |--------------------------------------------------------------------------
    | CANCEL
    |--------------------------------------------------------------------------
    */

    const handleCancel =
        async (id) => {

            if (
                !window.confirm(
                    "Cancel this collection?"
                )
            ) {

                return;

            }


            try {

                await cancelCollection(
                    id
                );


                showSuccess(
                    "Collection cancelled successfully."
                );


                await refreshAll();

            } catch (error) {

                console.error(
                    "Cancel error:",
                    error
                );


                showError(
                    error?.response?.data?.message ||
                    "Unable to cancel collection."
                );

            }

        };



    /*
    |--------------------------------------------------------------------------
    | SUBMIT
    |--------------------------------------------------------------------------
    */

    const handleSubmit =
        async (id) => {

            if (
                !window.confirm(
                    "Submit this collection for verification?"
                )
            ) {

                return;

            }


            try {

                await submitCollection(
                    id
                );


                showSuccess(
                    "Collection submitted successfully."
                );


                await refreshAll();

            } catch (error) {

                console.error(
                    "Submit error:",
                    error
                );


                showError(
                    error?.response?.data?.message ||
                    "Unable to submit collection."
                );

            }

        };



    /*
    |--------------------------------------------------------------------------
    | VERIFY
    |--------------------------------------------------------------------------
    */

    const handleVerify =
        async (id) => {

            if (
                !window.confirm(
                    "Verify this collection?"
                )
            ) {

                return;

            }


            const remarks =
                window.prompt(
                    "Verification remarks (optional)"
                );


            try {

                await verifyCollection(
                    id,
                    {
                        remarks:
                            remarks?.trim() ||
                            undefined,
                    }
                );


                showSuccess(
                    "Collection verified successfully."
                );


                await refreshAll();

            } catch (error) {

                console.error(
                    "Verify error:",
                    error
                );


                showError(
                    error?.response?.data?.message ||
                    "Unable to verify collection."
                );

            }

        };



    /*
    |--------------------------------------------------------------------------
    | REJECT
    |--------------------------------------------------------------------------
    */

    const handleReject =
        async (id) => {

            const reason =
                window.prompt(
                    "Rejection reason"
                );


            if (
                !reason?.trim()
            ) {

                return;

            }


            try {

                await rejectCollection(
                    id,
                    reason.trim()
                );


                showSuccess(
                    "Collection rejected successfully."
                );


                await refreshAll();

            } catch (error) {

                console.error(
                    "Reject error:",
                    error
                );


                showError(
                    error?.response?.data?.message ||
                    "Unable to reject collection."
                );

            }

        };



    /*
    |--------------------------------------------------------------------------
    | OPENING BALANCE
    |--------------------------------------------------------------------------
    */

    const handleOpeningBalance =
        () => {

            if (
                !canManageOpeningBalance
            ) {

                showError(
                    "Only Admin can create opening balances."
                );

                return;

            }


            setOpenOpeningModal(
                true
            );

        };



    /*
    |--------------------------------------------------------------------------
    | RENDER
    |--------------------------------------------------------------------------
    */

    return (

        <div className="mt-5 space-y-6">


            {/* ==========================================================
          PAGE HEADER
      =========================================================== */}

            <div
                className="
          flex
          flex-col
          gap-4
          rounded-2xl
          bg-white
          p-5
          shadow-sm
          md:flex-row
          md:items-center
          md:justify-between
        "
            >

                <div>

                    <h1
                        className="
              text-2xl
              font-bold
              text-navy-700
            "
                    >
                        Payment / Collections
                    </h1>


                    <p
                        className="
              mt-1
              text-sm
              text-gray-500
            "
                    >
                        Manage collections, opening balances
                        and outstanding amounts.
                    </p>

                </div>


                <div
                    className="
            rounded-full
            bg-blue-50
            px-4
            py-2
            text-xs
            font-semibold
            capitalize
            text-blue-700
          "
                >
                    {roles.length
                        ? roles.join(", ")
                        : "No role"
                    }
                </div>

            </div>



            {/* ==========================================================
          QUICK ACTIONS
      =========================================================== */}

            <div
                className="
          grid
          grid-cols-1
          gap-4
          md:grid-cols-2
        "
            >

                {canCreateCollection && (

                    <button
                        type="button"
                        onClick={
                            handleAdd
                        }
                        className="
              rounded-2xl
              bg-blue-600
              p-5
              text-left
              text-white
              shadow-sm
              transition
              hover:bg-blue-700
            "
                    >

                        <div
                            className="
                text-lg
                font-bold
              "
                        >
                            + Add Collection
                        </div>


                        <div
                            className="
                mt-1
                text-sm
                opacity-90
              "
                        >
                            Record money received
                            from a school or party.
                        </div>

                    </button>

                )}



                {canManageOpeningBalance && (

                    <button
                        type="button"
                        onClick={
                            handleOpeningBalance
                        }
                        className="
              rounded-2xl
              bg-indigo-600
              p-5
              text-left
              text-white
              shadow-sm
              transition
              hover:bg-indigo-700
            "
                    >

                        <div
                            className="
                text-lg
                font-bold
              "
                        >
                            + Opening Balance
                        </div>


                        <div
                            className="
                mt-1
                text-sm
                opacity-90
              "
                        >
                            Set the starting balance.
                            Outstanding is updated automatically.
                        </div>

                    </button>

                )}

            </div>



            {/* ==========================================================
          BALANCE SUMMARY
      =========================================================== */}

            <div>

                <div
                    className="
            mb-4
            flex
            items-center
            justify-between
          "
                >

                    <div>

                        <h2
                            className="
                text-xl
                font-bold
                text-navy-700
              "
                        >
                            Balance Summary
                        </h2>


                        <p
                            className="
                mt-1
                text-sm
                text-gray-500
              "
                        >
                            Current financial position.
                        </p>

                    </div>


                    {balanceLoading && (

                        <span
                            className="
                text-xs
                text-gray-500
              "
                        >
                            Loading balances...
                        </span>

                    )}

                </div>


                <div
                    className="
            grid
            grid-cols-1
            gap-4
            md:grid-cols-3
          "
                >

                    {/* OPENING */}

                    <BalanceCard
                        title="Opening Balance"
                        amount={
                            balanceSummary.openingTotal
                        }
                        subtitle="Starting balances"
                        className="bg-indigo-600"
                        formatMoney={formatMoney}
                    />


                    {/* OUTSTANDING */}

                    <BalanceCard
                        title="Outstanding"
                        amount={
                            balanceSummary.outstandingTotal
                        }
                        subtitle="Current amount due"
                        className="bg-amber-500"
                        formatMoney={formatMoney}
                    />


                    {/* COLLECTED */}

                    <BalanceCard
                        title="Collected"
                        amount={
                            balanceSummary.collectedTotal
                        }
                        subtitle="Verified collections"
                        className="bg-emerald-600"
                        formatMoney={formatMoney}
                    />

                </div>

            </div>



            {/* ==========================================================
          OPENING BALANCES
      =========================================================== */}

            {(isAdmin || isManager) && (

                <div
                    className="
            overflow-hidden
            rounded-2xl
            bg-white
            shadow-sm
          "
                >

                    <div
                        className="
              border-b
              border-gray-100
              px-5
              py-4
            "
                    >

                        <h2
                            className="
                text-lg
                font-bold
                text-navy-700
              "
                        >
                            Opening Balances
                        </h2>


                        <p
                            className="
                mt-1
                text-xs
                text-gray-500
              "
                        >
                            Starting balances entered by Admin.
                        </p>

                    </div>


                    {openingBalances.length ? (

                        <div
                            className="
                overflow-x-auto
              "
                        >

                            <table
                                className="
                  w-full
                  min-w-[700px]
                  text-left
                "
                            >

                                <thead
                                    className="
                    bg-gray-50
                  "
                                >

                                    <tr>

                                        <th className="px-5 py-3 text-xs font-semibold uppercase text-gray-500">
                                            Customer
                                        </th>

                                        <th className="px-5 py-3 text-xs font-semibold uppercase text-gray-500">
                                            Type
                                        </th>

                                        <th className="px-5 py-3 text-xs font-semibold uppercase text-gray-500">
                                            Amount
                                        </th>

                                        <th className="px-5 py-3 text-xs font-semibold uppercase text-gray-500">
                                            Balance Date
                                        </th>

                                        <th className="px-5 py-3 text-xs font-semibold uppercase text-gray-500">
                                            Remarks
                                        </th>

                                    </tr>

                                </thead>


                                <tbody>

                                    {openingBalances.map(
                                        (balance) => (

                                            <tr
                                                key={
                                                    balance.id
                                                }
                                                className="
                          border-t
                          border-gray-100
                          hover:bg-gray-50
                        "
                                            >

                                                <td className="px-5 py-4">

                                                    <div
                                                        className="
                              font-semibold
                              text-gray-800
                            "
                                                    >
                                                        {
                                                            getCustomerName(
                                                                balance
                                                            )
                                                        }
                                                    </div>


                                                    <div
                                                        className="
                              mt-1
                              text-xs
                              text-gray-500
                            "
                                                    >

                                                        {
                                                            balance.party
                                                                ?.partyCode ||
                                                            balance.school
                                                                ?.schoolCode ||
                                                            "-"
                                                        }

                                                    </div>

                                                </td>


                                                <td className="px-5 py-4">

                                                    <span
                                                        className="
                              rounded-full
                              bg-gray-100
                              px-3
                              py-1
                              text-xs
                              font-semibold
                              capitalize
                            "
                                                    >
                                                        {
                                                            getCustomerType(
                                                                balance
                                                            )
                                                        }
                                                    </span>

                                                </td>


                                                <td
                                                    className="
                            px-5
                            py-4
                            font-bold
                            text-indigo-600
                          "
                                                >
                                                    ₹
                                                    {
                                                        formatMoney(
                                                            balance.amount
                                                        )
                                                    }
                                                </td>


                                                <td className="px-5 py-4 text-sm text-gray-600">

                                                    {
                                                        balance.balanceDate
                                                            ? new Date(
                                                                balance.balanceDate
                                                            ).toLocaleDateString()
                                                            : "-"
                                                    }

                                                </td>


                                                <td className="px-5 py-4 text-sm text-gray-600">

                                                    {
                                                        balance.remarks ||
                                                        "-"
                                                    }

                                                </td>

                                            </tr>

                                        )
                                    )}

                                </tbody>

                            </table>

                        </div>

                    ) : (

                        <div
                            className="
                px-5
                py-10
                text-center
                text-sm
                text-gray-500
              "
                        >
                            No opening balances found.
                        </div>

                    )}

                </div>

            )}



            {/* ==========================================================
          OUTSTANDING BALANCES
      =========================================================== */}

            <div
                className="
          overflow-hidden
          rounded-2xl
          bg-white
          shadow-sm
        "
            >

                <div
                    className="
            border-b
            border-gray-100
            px-5
            py-4
          "
                >

                    <h2
                        className="
              text-lg
              font-bold
              text-navy-700
            "
                    >
                        Outstanding Balances
                    </h2>


                    <p
                        className="
              mt-1
              text-xs
              text-gray-500
            "
                    >
                        Current amount still receivable.
                    </p>

                </div>


                {outstandingBalances.length ? (

                    <div
                        className="
              overflow-x-auto
            "
                    >

                        <table
                            className="
                w-full
                min-w-[850px]
                text-left
              "
                        >

                            <thead
                                className="
                  bg-gray-50
                "
                            >

                                <tr>

                                    <th className="px-5 py-3 text-xs font-semibold uppercase text-gray-500">
                                        Customer
                                    </th>

                                    <th className="px-5 py-3 text-xs font-semibold uppercase text-gray-500">
                                        Type
                                    </th>

                                    <th className="px-5 py-3 text-xs font-semibold uppercase text-gray-500">
                                        Total
                                    </th>

                                    <th className="px-5 py-3 text-xs font-semibold uppercase text-gray-500">
                                        Collected
                                    </th>

                                    <th className="px-5 py-3 text-xs font-semibold uppercase text-gray-500">
                                        Outstanding
                                    </th>

                                    <th className="px-5 py-3 text-xs font-semibold uppercase text-gray-500">
                                        Last Collection
                                    </th>

                                </tr>

                            </thead>


                            <tbody>

                                {outstandingBalances.map(
                                    (balance) => (

                                        <tr
                                            key={
                                                balance.id
                                            }
                                            className="
                        border-t
                        border-gray-100
                        hover:bg-gray-50
                      "
                                        >

                                            <td className="px-5 py-4">

                                                <div
                                                    className="
                            font-semibold
                            text-gray-800
                          "
                                                >
                                                    {
                                                        getCustomerName(
                                                            balance
                                                        )
                                                    }
                                                </div>


                                                <div
                                                    className="
                            mt-1
                            text-xs
                            text-gray-500
                          "
                                                >
                                                    {
                                                        balance.party
                                                            ?.partyCode ||
                                                        balance.school
                                                            ?.schoolCode ||
                                                        "-"
                                                    }
                                                </div>

                                            </td>


                                            <td className="px-5 py-4">

                                                <span
                                                    className="
                            rounded-full
                            bg-gray-100
                            px-3
                            py-1
                            text-xs
                            font-semibold
                            capitalize
                          "
                                                >
                                                    {
                                                        getCustomerType(
                                                            balance
                                                        )
                                                    }
                                                </span>

                                            </td>


                                            <td
                                                className="
                          px-5
                          py-4
                          font-semibold
                          text-gray-800
                        "
                                            >
                                                ₹
                                                {
                                                    formatMoney(
                                                        balance.totalAmount
                                                    )
                                                }
                                            </td>


                                            <td
                                                className="
                          px-5
                          py-4
                          font-semibold
                          text-emerald-600
                        "
                                            >
                                                ₹
                                                {
                                                    formatMoney(
                                                        balance.collectedAmount
                                                    )
                                                }
                                            </td>


                                            <td
                                                className="
                          px-5
                          py-4
                          text-base
                          font-bold
                          text-red-600
                        "
                                            >
                                                ₹
                                                {
                                                    formatMoney(
                                                        balance.balanceAmount
                                                    )
                                                }
                                            </td>


                                            <td
                                                className="
                          px-5
                          py-4
                          text-sm
                          text-gray-600
                        "
                                            >

                                                {
                                                    balance.lastCollectionDate
                                                        ? new Date(
                                                            balance.lastCollectionDate
                                                        ).toLocaleDateString()
                                                        : "-"
                                                }

                                            </td>

                                        </tr>

                                    )
                                )}

                            </tbody>

                        </table>

                    </div>

                ) : (

                    <div
                        className="
              px-5
              py-10
              text-center
              text-sm
              text-gray-500
            "
                    >
                        No outstanding balances found.
                    </div>

                )}

            </div>



            {/* ==========================================================
          COLLECTION TABLE
      =========================================================== */}

            <DataTable

                title="Payment / Collections"

                columns={
                    collectionColumns({

                        onEdit:
                            handleEdit,

                        onCancel:
                            handleCancel,

                        onSubmit:
                            handleSubmit,

                        onVerify:
                            handleVerify,

                        onReject:
                            handleReject,

                    })
                }

                data={
                    collections
                }

                loading={
                    loading
                }

                onAdd={
                    canCreateCollection
                        ? handleAdd
                        : undefined
                }

                addButtonText="Add Collection"

            />



            {/* ==========================================================
          COLLECTION MODAL
      =========================================================== */}

            <CollectionModal

                open={
                    openCollectionModal
                }

                onClose={
                    closeCollectionModal
                }

                reload={
                    refreshAll
                }

                item={
                    selectedCollection
                }

            />



            {/* ==========================================================
          OPENING BALANCE MODAL
      =========================================================== */}

            {canManageOpeningBalance && (

                <OpeningBalanceModal

                    open={
                        openOpeningModal
                    }

                    onClose={() =>
                        setOpenOpeningModal(
                            false
                        )
                    }

                    reload={
                        refreshAll
                    }

                />

            )}

        </div>

    );

}



/*
|--------------------------------------------------------------------------
| BALANCE CARD
|--------------------------------------------------------------------------
*/

function BalanceCard({
    title,
    amount,
    subtitle,
    className,
    formatMoney,
}) {

    return (

        <div
            className={`
        rounded-2xl
        p-5
        text-white
        shadow-sm
        ${className}
      `}
        >

            <div
                className="
          text-sm
          font-medium
          opacity-90
        "
            >
                {title}
            </div>


            <div
                className="
          mt-2
          text-3xl
          font-bold
        "
            >
                ₹
                {
                    formatMoney(
                        amount
                    )
                }
            </div>


            <div
                className="
          mt-2
          text-xs
          opacity-80
        "
            >
                {subtitle}
            </div>

        </div>

    );

}